import { TranslateService } from '@ngx-translate/core';
import { InboundWarrantStatus } from './inbound.config';

/**
 * Get status severity for PrimeNG Tag component
 * @param status - Status string or number
 * @returns Severity level for PrimeNG Tag
 */
export function getInboundStatusSeverity(
  status: string | number | undefined
): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
  if (status === undefined || status === null) return 'secondary';

  // Handle numeric status (1, 2, 3, etc.)
  const statusNum = typeof status === 'string' ? parseInt(status) : status;
  if (!isNaN(statusNum)) {
    const statusSeverity: {
      [key: number]: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
    } = {
      1: 'info', // CREATED
      2: 'info', // CONFIRMED
      3: 'warning', // IN_TRANSIT
      4: 'warning', // ARRIVED
      5: 'warning', // RECEIVING
      6: 'warning', // PARTIALLY_RECEIVED
      7: 'success', // RECEIVED
      8: 'success', // CLOSED
      9: 'danger', // CANCELLED
    };
    return statusSeverity[statusNum] || 'secondary';
  }

  // Handle string status
  const statusSeverity: {
    [key: string]: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
  } = {
    [InboundWarrantStatus.CREATED]: 'info',
    [InboundWarrantStatus.CONFIRMED]: 'info',
    [InboundWarrantStatus.IN_TRANSIT]: 'warning',
    [InboundWarrantStatus.ARRIVED]: 'warning',
    [InboundWarrantStatus.RECEIVING]: 'warning',
    [InboundWarrantStatus.PARTIALLY_RECEIVED]: 'warning',
    [InboundWarrantStatus.RECEIVED]: 'success',
    [InboundWarrantStatus.CANCELLED]: 'danger',
    [InboundWarrantStatus.PENDING]: 'secondary',
    [InboundWarrantStatus.INSPECTED]: 'info',
    [InboundWarrantStatus.COMPLETED]: 'success',
  };

  return statusSeverity[status] || 'secondary';
}

/**
 * Get status style for custom styling
 * @param status - Status string or number
 * @returns Style object with background-color and color
 */
export function getInboundStatusStyle(
  status: string | number | undefined
): { 'background-color': string; color: string; border: string } {
  const severity = getInboundStatusSeverity(status);

  switch (severity) {
    case 'success':
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    case 'warning':
      return {
        'background-color': '#fef3c7',
        color: '#d97706',
        border: 'none',
      };
    case 'danger':
      return {
        'background-color': '#fee2e2',
        color: '#dc2626',
        border: 'none',
      };
    case 'info':
      return {
        'background-color': '#dbeafe',
        color: '#1e40af',
        border: 'none',
      };
    case 'secondary':
    default:
      return {
        'background-color': '#f9fafb',
        color: '#6b7280',
        border: 'none',
      };
  }
}

/**
 * Get inbound type label (PO, TO, RO)
 * @param inputType - Input type string (PO, TO, RO)
 * @param translateService - TranslateService instance
 * @returns Translated label
 */
export function getInboundTypeLabel(
  inputType: string,
  translateService: TranslateService
): string {
  const typeMap: { [key: string]: string } = {
    PO: translateService.instant('inbound.types.purchase_order'),
    TO: translateService.instant('inbound.types.transfer_order'),
    RO: translateService.instant('inbound.types.return_order'),
  };
  return typeMap[inputType] || inputType; // Fallback to raw value if not found
}

/**
 * Get inbound status label
 * @param status - Status string or number
 * @param translateService - TranslateService instance
 * @returns Translated label
 */
export function getInboundStatusLabel(
  status: string | number | undefined,
  translateService: TranslateService
): string {
  if (status === undefined || status === null) return '-';

  // Handle numeric status (1, 2, 3, etc.)
  const statusNum = typeof status === 'string' ? parseInt(status) : status;
  if (!isNaN(statusNum)) {
    const statusMap: { [key: number]: string } = {
      1: translateService.instant('inbound.status.created'),
      2: translateService.instant('inbound.status.confirmed'),
      3: translateService.instant('inbound.status.in_transit'),
      4: translateService.instant('inbound.status.arrived'),
      5: translateService.instant('inbound.status.receiving'),
      6: translateService.instant('inbound.status.partially_received'),
      7: translateService.instant('inbound.status.received'),
      8: translateService.instant('inbound.status.closed'),
      9: translateService.instant('inbound.status.cancelled'),
    };
    return statusMap[statusNum] || String(status);
  }

  // Handle string status
  const statusMap: { [key: string]: string } = {
    CREATED: translateService.instant('inbound.status.created'),
    CONFIRMED: translateService.instant('inbound.status.confirmed'),
    IN_TRANSIT: translateService.instant('inbound.status.in_transit'),
    ARRIVED: translateService.instant('inbound.status.arrived'),
    RECEIVING: translateService.instant('inbound.status.receiving'),
    PARTIALLY_RECEIVED: translateService.instant(
      'inbound.status.partially_received'
    ),
    RECEIVED: translateService.instant('inbound.status.received'),
    CLOSED: translateService.instant('inbound.status.closed'),
    CANCELLED: translateService.instant('inbound.status.cancelled'),
    PENDING: translateService.instant('inbound.status.pending'),
    INSPECTED: translateService.instant('inbound.status.inspected'),
    COMPLETED: translateService.instant('inbound.status.completed'),
  };
  return statusMap[status] || String(status); // Fallback to raw value if not found
}
